import type {Branded} from '@kapitalist/utils/Branded'
import {brand} from '@kapitalist/utils/Branded'

/**
 * An email that names a real, DB-validated player (or pending signup) row.
 * Minted only from authoritative server state (a DB row). See {@link TUntrustedPlayerEmail}
 * for the opposite.
 */
export type TPlayerEmail = Branded<string, typeof playerEmailBrand>
export const mkPlayerEmail = (email: string): TPlayerEmail =>
  brand<TPlayerEmail>(email)
declare const playerEmailBrand: unique symbol

/** A username that names a real, DB-validated player (or pending signup) row. See {@link TPlayerEmail}. */
export type TPlayerUsername = Branded<string, typeof playerUsernameBrand>
export const mkPlayerUsername = (username: string): TPlayerUsername =>
  brand<TPlayerUsername>(username)
declare const playerUsernameBrand: unique symbol

/**
 * An email a client supplied that isn't known to actually match any player row. The backend
 * must look one up (or reject) rather than trusting it — see {@link TPlayerEmail}.
 */
export type TUntrustedPlayerEmail = Branded<string, typeof untrustedPlayerEmailBrand>
export const mkUntrustedPlayerEmail = (email: string): TUntrustedPlayerEmail =>
  brand<TUntrustedPlayerEmail>(email)
declare const untrustedPlayerEmailBrand: unique symbol

/** A username a client supplied that hasn't been checked against any player row. See {@link TUntrustedPlayerEmail}. */
export type TUntrustedPlayerUsername = Branded<
  string,
  typeof untrustedPlayerUsernameBrand
>
export const mkUntrustedPlayerUsername = (
  username: string
): TUntrustedPlayerUsername => brand<TUntrustedPlayerUsername>(username)
declare const untrustedPlayerUsernameBrand: unique symbol

/**
 * Either an email or a username, client-supplied / not yet known to match any player row.
 * See {@link TUntrustedPlayerEmail}.
 */
export type TUntrustedPlayerIdentifier = Branded<
  string,
  typeof untrustedPlayerIdentifierBrand
>
export const mkUntrustedPlayerIdentifier = (
  identifier: string
): TUntrustedPlayerIdentifier => brand<TUntrustedPlayerIdentifier>(identifier)
declare const untrustedPlayerIdentifierBrand: unique symbol
